// components/fallbacks/PageFallback.tsx
import * as Sentry from '@sentry/react';
import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import ImageComponent from 'src/components/common/Image';
import { ICON_NO_MATCH_SEARCH_COMPANY } from 'src/utils/constants/imagesUrl';

// boundaries/PageErrorBoundary.tsx
export const PageErrorBoundary = ({
  children,
  pageName,
  resetKey,
}: {
  children: ReactNode;
  pageName?: string;
  resetKey?: string;
}) => (
  <Sentry.ErrorBoundary
    key={resetKey || ''}
    fallback={<PageFallback />}
    beforeCapture={(scope, event: ExpectedAny) => {
      event.tags = {
        ...event.tags,
        boundary: 'Page Error',
        ...(pageName && { pageName }),
      };
    }}
  >
    {children}
  </Sentry.ErrorBoundary>
);

function PageFallback() {
  const navigate = useNavigate();
  const theme = useTheme();
  return (
    <Stack padding="48px 24px" justifyContent="center" direction="row">
      <Box
        bgcolor={theme.palette.background.paper}
        borderRadius={theme.shape.borderRadius}
        padding={4}
        width="100%"
        boxShadow="0 6px 20px rgba(30, 30, 60, 0.06)"
        textAlign="center"
      >
        <Stack direction="row" justifyContent="center" spacing={6}>
          <ImageComponent imgSrc={ICON_NO_MATCH_SEARCH_COMPANY} height={150} />
          <Stack direction="column" spacing={1} justifyContent="center">
            <Typography mb={1} color="text.primary" variant="h6">
              We're preparing the data for this page
            </Typography>
            <Typography mb={3} variant="body1">
              The information on this page is not fully available yet.
            </Typography>
            <Typography mb={3} variant="body1">
              Please give us a moment or try refreshing the page.
            </Typography>
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="center" spacing={1.5}>
          <Button onClick={() => navigate('/')} variant="outlined" color="info" size="small">
            Go to Homepage
          </Button>
          <Button onClick={() => window.location.reload()} variant="contained" color="primary" size="small">
            Reload
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
}
